import {
  ActivityFilterOptions,
  ActivityFilters,
  activityFiltersToApiQuery,
  asActivityFilterOptions,
  filenameFromContentDisposition,
} from "./activity-filters";
import {
  ActivitySearchInterpreted,
  asActivitySearchInterpreted,
  viewerTimeZone,
} from "./activity-search";
import { ACTIVITY_PAGE_SIZE, AuditPage, asAuditPage } from "./audit-history";
import { getApi } from "./get-api";

/**
 * Client for the audit routes (MOSA-368).
 *
 * - GET /audit/:datasourceId/activity?page=&limit=&actor=&tab=&from=&to=&source=&q=&tz=
 * - GET /audit/:datasourceId/activity/filters
 * - GET /audit/:datasourceId/activity/export.csv (same filter params, including q and tz)
 * - GET /audit/:datasourceId/:collection/rows/:rowKey?page=&limit=
 *
 * Hand-written rather than generated: the generated client is rebuilt from the
 * running API's swagger, and these reads are needed before that happens.
 */

type AuditRequest = { token?: string | null; timeZone?: string };

export type ActivityListPage = AuditPage & {
  interpreted: ActivitySearchInterpreted | null;
};

const api = (token: string | null | undefined) => getApi({ token }) as any;

const getJson = (token: string | null | undefined, path: string) =>
  api(token).request({ path, method: "GET", format: "json" });

export const fetchActivity = async (
  datasourceId: string,
  page: number,
  options: AuditRequest & { filters?: ActivityFilters } = {}
): Promise<ActivityListPage> => {
  const query = activityFiltersToApiQuery(options.filters ?? {}, {
    page,
    limit: ACTIVITY_PAGE_SIZE,
    timeZone: options.timeZone ?? viewerTimeZone(),
  });
  const response = await getJson(
    options.token,
    `/audit/${encodeURIComponent(datasourceId)}/activity?${query}`
  );
  const data = response?.data;
  return {
    ...asAuditPage(data),
    interpreted: asActivitySearchInterpreted(
      data && typeof data === "object"
        ? (data as { interpreted?: unknown }).interpreted
        : null
    ),
  };
};

export const fetchActivityFilters = async (
  datasourceId: string,
  options: AuditRequest = {}
): Promise<ActivityFilterOptions> => {
  const response = await getJson(
    options.token,
    `/audit/${encodeURIComponent(datasourceId)}/activity/filters`
  );
  return asActivityFilterOptions(response?.data);
};

export type ActivityCsvExport = {
  blob: Blob;
  filename: string;
  truncated: boolean;
};

const EXPORT_TRUNCATED_HEADER = "x-onlydash-export-truncated";

/**
 * Download the Activity CSV with the current filters.
 *
 * Uses the auth header from getApi. The file name comes from
 * Content-Disposition. Truncation is the X-OnlyDash-Export-Truncated header.
 */
export const exportActivityCsv = async (
  datasourceId: string,
  filters: ActivityFilters,
  options: AuditRequest = {}
): Promise<ActivityCsvExport> => {
  const query = activityFiltersToApiQuery(filters, {
    timeZone: options.timeZone ?? viewerTimeZone(),
  });
  const path = `/audit/${encodeURIComponent(
    datasourceId
  )}/activity/export.csv${query ? `?${query}` : ""}`;
  const response = await api(options.token).request({
    path,
    method: "GET",
    format: "blob",
  });
  const headers = (response?.headers ?? {}) as Record<string, string>;
  const disposition =
    headers["content-disposition"] ?? headers["Content-Disposition"];
  const truncatedRaw =
    headers[EXPORT_TRUNCATED_HEADER] ?? headers["X-OnlyDash-Export-Truncated"];
  const filename =
    filenameFromContentDisposition(disposition) ||
    "onlydash-activity.csv";
  return {
    blob: response?.data as Blob,
    filename,
    truncated: String(truncatedRaw).toLowerCase() === "true",
  };
};

/** Trigger a browser download for a Blob without navigating away. */
export const saveBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const fetchRowHistory = async (
  datasourceId: string,
  collection: string,
  rowKey: string,
  options: AuditRequest = {}
): Promise<AuditPage> => {
  const response = await getJson(
    options.token,
    `/audit/${encodeURIComponent(datasourceId)}/${encodeURIComponent(
      collection
    )}/rows/${encodeURIComponent(rowKey)}?page=1&limit=${ACTIVITY_PAGE_SIZE}`
  );
  return asAuditPage(response?.data);
};
